import React from "react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { PageHeadline } from "../../components/_shared-components/ui-elements/PageHeadline";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";

import {
  ExtractSwarovskiPageData,
  ProductComparisonPageData,
  SwarovskiOptikLocaleFrontend,
  ProductComparisonResponseData,
  type TranslatedValue,
} from "@swo/storefront";
import { css } from "@emotion/react";

import { option } from "fp-ts";
import { Option } from "fp-ts/es6/Option";
import { pipe } from "fp-ts/lib/function";
import { usePCPState } from "./elements/state";
import { ProductSelectionDropdown } from "./elements/ProductSelectionDropdown";
import { AttributeDisplayer } from "./elements/AttributeDisplayer";
import { ModalLoadingSpinner } from "../../routes/modal-loading-spinner";
import { gridStyles } from "./styles";
import { PCPProductTeaser } from "./elements/PCPProductTeaser";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { CSS_MQ } from "../../styles/breakpoints";
import { CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { useExternalState } from "../../hooks";
import { T } from "../../components/T";

export type ProductKey = string;

export type StateT = {
  for: string;
  selectedProducts: NonEmptyArray<ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["products"][0]>;
  mode: "all" | "differences-only";
};

export type RawStateT = {
  for: string;
  selectedProducts: Array<ProductKey>;
  mode: "all" | "differences-only";
};

export const ProductComparisonPageComponent: ReactTFC<{
  translations: ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["translations"];
  products: ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["products"];
  fetcherByProductKeys: (productKeys: Array<string>) => Promise<Option<ProductComparisonResponseData>>;
  countryIsoCode: string;
  fromPriceLabelTranslation: TranslatedValue;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
}> = props => {
  const [state, selectedProductsUpdated, selectedNewProduct, onRemoveRequested, toggleMode] = usePCPState(
    props.products,
  );
  const selectedProductKeys = state.selectedProducts.map(p => p.product.key);
  const selectedCount = selectedProductKeys.length;

  const externalState = useExternalState(selectedProductKeys, () => props.fetcherByProductKeys(selectedProductKeys));

  return (
    <React.Fragment>
      <ContentContainer>
        <>
          <title>{state.selectedProducts.map(p => p.product.name).join(" vs ")}</title>
        </>

        <PageHeadline>
          <EXPERIMENTAL_TextWithProductNameRenderer text={props.translations.productType} />
          {` - `}
          <T>{props.translations.comparison}</T>
        </PageHeadline>
      </ContentContainer>

      <div
        css={css`
          ${CSS_MQ.atMostSm} {
            overflow-x: auto;

            padding-top: 20px;
            margin-top: -20px;
          }
        `}
      >
        <ProductSelectionDropdown
          selectedProducts={state.selectedProducts}
          products={props.products}
          selectedProductsUpdated={selectedProductsUpdated}
          selectedNewProduct={selectedNewProduct}
          onRemoveRequested={onRemoveRequested}
          selectedCount={selectedCount}
          compareLabel={<T>{props.translations.compareLabel}</T>}
        />

        <ContentContainer>
          <div
            className={`selected-${selectedCount}`}
            css={[
              gridStyles,
              css`
                margin-top: ${CSS_SPACER_SIZES.md};
              `,
            ]}
          >
            {state.selectedProducts.map(p => {
              return (
                <PCPProductTeaser
                  key={p.product.key}
                  product={p}
                  buyCallToActionLabel={props.translations.buyCallToActionLabel}
                  fromPriceLabelTranslation={props.fromPriceLabelTranslation}
                  localeForNumberFormat={props.localeForNumberFormat}
                />
              );
            })}
          </div>

          {externalState.__tag === "loaded"
            ? pipe(
                externalState.data.result,
                option.map(data => {
                  return (
                    <AttributeDisplayer
                      sections={data.sections}
                      selectedProductKeys={selectedProductKeys}
                      mode={state.mode}
                      toggleMode={toggleMode}
                      showOnlyDifferencesLabel={<T>{props.translations.showOnlyDifferencesLabel}</T>}
                    />
                  );
                }),
                option.getOrElse(() => (
                  <div
                    css={css`
                      text-align: center;
                      font-size: 22px;
                      font-weight: 300;
                      width: 30em;
                      margin: 100px auto 50px;
                    `}
                  >
                    <T>{props.translations.unexpectedErrorOccurred}</T>
                  </div>
                )),
              )
            : null}

          {externalState.__tag === "loading" && (
            <React.Fragment>
              <ModalLoadingSpinner />
              <div
                css={css`
                  height: 1000px;
                `}
              />
            </React.Fragment>
          )}
        </ContentContainer>
      </div>
    </React.Fragment>
  );
};
